import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { JOIN_NOTE_MAX, paceBand, PACE_BANDS, type PublicClub } from "@pagemates/shared";
import { askForPush } from "./PushAsk";
import { api, describeError } from "../lib/api";
import { Avatar, Spinner, useScrollLock, useSheetDrag } from "./ui";
import { Cover } from "./BookBits";
import { BackBar } from "./ui";
import { CloseIcon } from "./Icons";
import { Wordmark } from "./Wordmark";

/*
 * A public club as a non-member sees it (PLAN.md §Public clubs): the invite
 * page's layout, built only from GET /clubs/:id/public. Open clubs: Join, then
 * a confirmation sheet. Request clubs: Ask to join with an optional note, then
 * Requested (cancel any time), or Not accepted until they can ask again.
 */
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const monthYear = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
const cadence = (d: number) => d < 10 ? "About every week" : d < 18 ? "About every two weeks" : d < 25 ? "About every three weeks" : d < 40 ? "About every month" : `About every ${Math.round(d / 7)} weeks`;
const PICK: Record<PublicClub["pickPolicy"], string> = { admins: "An admin picks", vote: "The club votes", turns: "Members take turns" };
const names = (ns: string[]) => ns.length <= 1 ? ns[0] ?? "" : `${ns.slice(0, -1).join(", ")} and ${ns[ns.length - 1]}`;

/** `signedOut`: someone followed the club's link without an account here; the action is signing in, which brings them back. */
export function PublicClubPage({ clubId, signedOut }: { clubId: string; signedOut?: boolean }) {
  const q = useQuery({ queryKey: ["public-club", clubId], queryFn: () => api.publicClub(clubId), retry: false });
  const [sheet, setSheet] = useState<"join" | "ask" | null>(null);
  if (q.isPending) return <main className="page"><Spinner /></main>;
  if (q.isError) return null; // the caller shows the private page
  const c = q.data;
  const band = paceBand(c.usualPace);
  const leaders = c.leaders.map((l) => l.name);
  const action = signedOut
    ? <Link to="/signin" search={{ next: `/clubs/${c.slug}` }} className="btn primary big">Sign in to join</Link>
    : <ClubAction c={c} onJoin={() => setSheet("join")} onAsk={() => setSheet("ask")} />;
  return (
    <main className="page pub-page">
      {signedOut
        ? <header className="topbar"><a href="/" aria-label="Pagemates home"><Wordmark size="sm" /></a><a href={`/signin?next=${encodeURIComponent(`/clubs/${c.slug}`)}`} className="small">Sign in</a></header>
        : <BackBar label="Find a club" link={{ to: "/clubs/browse" }} />}
      <header className="pub-head">
        <div className="eyebrow">{c.visibility === "open" ? "Open · anyone can join" : "Request to join"}</div>
        <h1>{c.name}</h1>
        {c.description && <p className="tag">{c.description}</p>}
        {c.genres.length > 0 && <div className="pills">{c.genres.map((g) => <span key={g} className="pill">{g}</span>)}</div>}
        <div className="pub-who">
          <span className="faces">{c.leaders.slice(0, 3).map((l) => <Avatar key={l.id} name={l.name} image={l.image} />)}</span>
          <span>{names(leaders)} {leaders.length === 1 ? "runs it" : "run it"} · {c.memberCount} {c.memberCount === 1 ? "member" : "members"} · since {monthYear(c.foundedAt)}</span>
        </div>
      </header>
      {/* With nothing for the main column yet (no book, shelf or house rules), the facts stand alone. */}
      <div className={`pub-cols${!c.current && c.shelf.count === 0 && !c.rules ? " solo" : ""}`}>
        {(c.current || c.shelf.count > 0 || c.rules) && <div className="pub-main">
          {c.current && (
            <div className="pub-now">
              <Cover book={{ hardcoverId: 0, title: c.current.title, authors: c.current.authors, coverUrl: c.current.coverUrl, pages: c.current.pages, releaseYear: null }} size="lg" />
              <div><span className="k">Reading now</span><b className="serif">{c.current.title}</b><div className="muted small">{c.current.authors.join(", ")}{c.current.pages ? ` · ${c.current.pages} pages` : ""}</div></div>
            </div>
          )}
          {c.shelf.count > 0 && (
            <div className="pub-shelf">
              <span className="k">The shelf so far</span>
              <div className="row" style={{ gap: 6 }}>
                {c.shelf.covers.map((b, i) => <Cover key={i} book={{ hardcoverId: i, title: b.title, authors: [], coverUrl: b.coverUrl, pages: null, releaseYear: null }} size="sm" />)}
                {c.shelf.count > c.shelf.covers.length && <span className="cover sm more">+{c.shelf.count - c.shelf.covers.length}</span>}
              </div>
            </div>
          )}
          {c.rules && <div className="jrules"><span className="k">How this club works</span>{c.rules}</div>}
        </div>}
        <aside className="pub-facts">
          {c.usualPace && <div className="fact"><span className="k">Pace</span>{band ? `${PACE_BANDS.find((b) => b.key === band)!.label} · ` : ""}about {c.usualPace} pages a day</div>}
          <div className="fact"><span className="k">Picking</span>{PICK[c.pickPolicy]}</div>
          {c.cadenceDays && <div className="fact"><span className="k">Meets</span>{cadence(c.cadenceDays)}</div>}
          <div className="fact"><span className="k">Spoilers</span>{c.spoilerGating ? "Hidden until you get there" : <span className="notice">Not hidden</span>}</div>
          <div className="pub-cta">{action}</div>
        </aside>
      </div>
      {/* Phones: the action stays in reach at the foot of the screen. */}
      <div className="pub-foot">{action}</div>
      {sheet === "join" && <JoinSheet c={c} onClose={() => setSheet(null)} />}
      {sheet === "ask" && <AskSheet c={c} onClose={() => setSheet(null)} />}
    </main>
  );
}

function ClubAction({ c, onJoin, onAsk }: { c: PublicClub; onJoin: () => void; onAsk: () => void }) {
  const qc = useQueryClient();
  const cancel = useMutation({ mutationFn: () => api.cancelRequest(c.id), onSuccess: () => qc.invalidateQueries({ queryKey: ["public-club"] }) });
  if (c.viewer.member) return <Link to="/clubs/$clubId" params={{ clubId: c.slug }} className="btn primary big">Open the club</Link>;
  if (c.visibility === "open") return <button type="button" className="btn primary big" onClick={onJoin}>Join the club</button>;
  const r = c.viewer.request;
  if (r?.status === "pending") return (
    <div className="req-state">
      <b>Requested</b><span className="muted small">An admin will decide. You can cancel any time.</span>
      <button type="button" className="linkish small" disabled={cancel.isPending} onClick={() => cancel.mutate()}>Cancel request</button>
    </div>
  );
  if (r?.status === "declined") return (
    <div className="req-state"><b>Not accepted</b><span className="muted small">You can ask again after {day(r.againAt!)}.</span><Link to="/clubs/browse" className="small">Find another club</Link></div>
  );
  return <button type="button" className="btn primary big" onClick={onAsk}>Ask to join</button>;
}

function Sheet({ label, onClose, children }: { label: string; onClose: () => void; children: React.ReactNode }) {
  useScrollLock();
  const ref = useRef<HTMLDivElement>(null);
  useSheetDrag(ref, onClose);
  return createPortal(
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className="modal pub-sheet" role="dialog" aria-label={label} ref={ref}>
        <div className="grab" />
        <div className="head"><b>{label}</b><button type="button" aria-label="Close" onClick={onClose}><CloseIcon /></button></div>
        <div className="pub-sheet-body">{children}</div>
      </div>
    </>,
    document.body,
  );
}

export function JoinSheet({ c, onClose }: { c: PublicClub; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const join = useMutation({
    mutationFn: () => api.joinClub(c.id),
    onSuccess: async (r) => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["me"] }), qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["public-club"] })]);
      onClose();
      void navigate({ to: "/clubs/$clubId", params: { clubId: r.clubSlug } }); // the Reading tab
    },
  });
  return (
    <Sheet label={`Join ${c.name}?`} onClose={onClose}>
      {c.rules && <div className="jrules"><span className="k">How this club works</span>{c.rules}</div>}
      <p className="small" style={{ margin: 0 }}>Members will see your name, photo and reading progress.</p>
      {join.isError && <p className="error small">{describeError(join.error)}</p>}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary" disabled={join.isPending} onClick={() => join.mutate()}>{join.isPending ? "Joining…" : "Join"}</button>
        <button type="button" className="btn ghost" onClick={onClose}>Not now</button>
      </div>
    </Sheet>
  );
}

export function AskSheet({ c, onClose }: { c: PublicClub; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const ask = useMutation({
    mutationFn: () => api.requestJoin(c.id, note.trim() || null),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["public-club"] }); onClose(); },
  });
  const left = JOIN_NOTE_MAX - note.length;
  return (
    <Sheet label={`Ask to join ${c.name}`} onClose={onClose}>
      <div className="field"><label htmlFor="ask-note">A note for the admins (optional)</label>
        <textarea id="ask-note" className="input" rows={3} maxLength={JOIN_NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this club, in a line or two." />
        <span className={`small num ${left < 20 ? "warn" : "muted"}`} style={{ alignSelf: "flex-end" }}>{left}</span></div>
      <p className="small" style={{ margin: 0 }}>If you're let in, members will see your name, photo and reading progress.</p>
      {ask.isError && <p className="error small">{describeError(ask.error)}</p>}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary" disabled={ask.isPending} onClick={() => ask.mutate()}>{ask.isPending ? "Sending…" : "Send request"}</button>
        <button type="button" className="btn ghost" onClick={onClose}>Not now</button>
      </div>
    </Sheet>
  );
}

/** Owners and admins: who's asking to join, at the top of the members column. */
export function JoinRequests({ clubId }: { clubId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["join-requests", clubId], queryFn: () => api.joinRequests(clubId) });
  const decide = useMutation({
    mutationFn: (v: { id: string; approve: boolean }) => api.decideRequest(clubId, v.id, v.approve),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["join-requests", clubId] }), qc.invalidateQueries({ queryKey: ["members", clubId] }), qc.invalidateQueries({ queryKey: ["club", clubId] })]),
  });
  const waiting = (q.data?.length ?? 0) > 0;
  useEffect(() => { if (waiting) askForPush("requests"); }, [waiting]); // the first request an owner or admin sees
  if (!q.data || q.data.length === 0) return null;
  return (
    <>
      <div className="mgrp req-h">Asking to join <span className="req-badge">{q.data.length}</span></div>
      <div className="mlist">
        {q.data.map((r) => (
          <div key={r.id} className="req-row">
            <Avatar name={r.user.name} image={r.user.image} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div><b>{r.user.name}</b> <span className="muted small">· {day(r.createdAt)}</span></div>
              {r.note && <div className="small req-note">“{r.note}”</div>}
            </div>
            <div className="row" style={{ gap: 6, flex: "none" }}>
              <button type="button" className="btn sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, approve: true })}>Let in</button>
              <button type="button" className="btn ghost sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, approve: false })}>Decline</button>
            </div>
          </div>
        ))}
      </div>
      {decide.isError && <p className="error small">{describeError(decide.error)}</p>}
    </>
  );
}


/** Owners and admins: who's blocked, at the bottom of the members column. Unblocking doesn't re-add anyone. */
export function BlockedList({ clubId }: { clubId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["blocks", clubId], queryFn: () => api.blocks(clubId) });
  const lift = useMutation({ mutationFn: (userId: string) => api.unblock(clubId, userId), onSuccess: () => qc.invalidateQueries({ queryKey: ["blocks", clubId] }) });
  if (!q.data || q.data.length === 0) return null;
  return (
    <>
      <div className="mgrp">Blocked · {q.data.length}</div>
      <div className="mlist">
        {q.data.map((b) => (
          <div key={b.user.id} className="req-row">
            <Avatar name={b.user.name} image={b.user.image} />
            <div style={{ minWidth: 0, flex: 1 }}><b>{b.user.name}</b> <span className="muted small">· since {day(b.blockedAt)}</span></div>
            <button type="button" className="btn ghost sm" disabled={lift.isPending} onClick={() => lift.mutate(b.user.id)}>Unblock</button>
          </div>
        ))}
      </div>
      <p className="muted small" style={{ margin: 0 }}>Unblocking doesn't add anyone back. They can return by invite, or join or ask like anyone else.</p>
      {lift.isError && <p className="error small">{describeError(lift.error)}</p>}
    </>
  );
}
